import React, { useEffect } from 'react'
import { convertDataBasedOnDate, convertDataBasedOnCompany } from 'helpers/TableComponentHelper'
import Loader from 'components/loader/Loader'
import SectionError from 'components/error/SectionError'
import { useFetch } from 'hooks/useFetch'
import { textBold } from 'utils/Constant'
import { Tooltip } from '@mui/material'
import { capitalize } from 'proptypes/StringProptype';

/* ── design tokens ── */
const T = {
  bg:   '#F6F5F0',
  card: '#FFFFFF',
  line: '#E5E2D8',
  ink:  '#232821',
  mut:  '#6E766C',
  acc:  '#2E9E7A',
};

const TableContainer = (props) => {
  const { url, title, unWantedKeys, isCompany, labelMapping, highlightSymbol, isQuarterly } = props
  const { data: rawData, loading, error, retry } = useFetch(url)
  const data = rawData || []

  const { columnHeaders, rows } = isCompany
    ? convertDataBasedOnCompany(data, unWantedKeys)
    : convertDataBasedOnDate(data, unWantedKeys)

  return (
    <div style={{
      background: T.card,
      borderRadius: 16,
      border: `1px solid ${T.line}`,
      padding: '18px 20px',
      marginBottom: 16,
      overflowX: 'auto',
    }}>
      {/* Card header */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <span style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: 16,
          fontWeight: 700,
          color: T.ink,
        }}>
          {title}
        </span>
        <span style={{ fontSize: 11, color: T.mut }}>
          Consolidated · Figures in ₹ Cr
        </span>
      </div>

      {loading ? (
        <Loader />
      ) : error ? (
        <SectionError message={error.message} onRetry={retry} />
      ) : data.length ? (
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: "'Instrument Sans', system-ui, sans-serif",
        }}>
          {/* ── thead ── */}
          <thead>
            <tr>
              {columnHeaders && columnHeaders.map((col, i) => {
                const key = col.headerName || col.analysisName || col.field || ''
                const mappedObj = labelMapping && labelMapping[key]
                const label = mappedObj ? mappedObj.acronym : key
                
                const isFY = /^\d{4}-\d{2}-\d{2}/.test(label)
                
                let display = label === 'PARTICULARS' ? '₹ CR' : label.replace(/_/g, ' ').toUpperCase()
                if (isFY) {
                    if (isQuarterly) {
                        const dateObj = new Date(label);
                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        display = monthNames[dateObj.getMonth()] + '-' + label.substring(2, 4);
                    } else {
                        display = 'FY' + label.substring(2, 4);
                    }
                }
                  
                const thElement = (
                  <th key={i} style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    color: T.mut,
                    paddingBottom: 10,
                    borderBottom: `1px solid ${T.line}`,
                    paddingRight: i === 0 ? 0 : 0,
                    whiteSpace: 'nowrap',
                    minWidth: i === 0 ? 160 : 80,
                  }}>
                    {display}
                  </th>
                )

                if (mappedObj && mappedObj.full) {
                  return (
                    <Tooltip key={i} title={mappedObj.full} arrow placement="top" componentsProps={{
                      tooltip: {
                          sx: {
                              color: '#0d0d0d',
                              fontSize: '11px',
                              fontWeight: '600',
                              bgcolor: '#ebf0ff',
                              textTransform: 'capitalize',
                              '& .MuiTooltip-arrow': { color: '#ebf0ff' },
                          },
                      },
                    }}>
                      {thElement}
                    </Tooltip>
                  )
                }

                return thElement;
              })}
            </tr>
          </thead>

          {/* ── tbody ── */}
          <tbody>
            {rows.map((row, ri) => {
              const particulars = row['PARTICULARS'] || ''
              const isBold = textBold[particulars]
              const isHighlighted = highlightSymbol && data[ri] && data[ri].symbol === highlightSymbol;
              return (
                <tr key={ri} style={{ 
                  borderBottom: `1px solid ${T.line}`,
                  backgroundColor: isHighlighted ? '#F1F7F4' : 'transparent',
                }}>
                  {columnHeaders && columnHeaders.map((col, ci) => {
                    const key = col.headerName || col.analysisName || col.field || ''
                    const val = row[key]
                    const isFirst = ci === 0

                    let tdColor = T.ink;
                    if (key === 'sales_growth') {
                        const num = parseFloat(String(val).replace(/,/g, ''));
                        if (!isNaN(num)) {
                            tdColor = num < 0 ? '#d32f2f' : T.acc;
                        }
                    }

                    return (
                      <td key={ci} style={{
                        textAlign: isFirst ? 'left' : 'right',
                        fontSize: 13,
                        fontWeight: isBold || (isHighlighted && isFirst) ? 700 : 400,
                        color: tdColor,
                        padding: '9px 0',
                        whiteSpace: 'nowrap',
                      }}>
                        {isFirst
                          ? (typeof val === 'string' ? capitalize(val.replace(/_/g, ' ')) : val)
                          : val}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        <div style={{ color: T.mut, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
          No data available
        </div>
      )}
    </div>
  )
}

export default TableContainer
